import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ViewHeader from "../utils/view-header";
import { capitaliseFirstChar, singlify } from "utils/utils";
import ClassesList from "./ClassesList";
import { MainContext } from "components/utils/Contexts";
import {
  deleteRecordRequest,
  getCollectionRequest,
  updateRecordRequest,
} from "services/apiRequests";
import {
  ClassListType,
  TeacherListType,
  ParentListType,
  StudentListType,
  ContactListType,
  CreateEditContactType,
} from "utils/interfaces";
import { AnimatePresence } from "framer-motion";
import { MdOutlinePerson } from "react-icons/md";
import { usePlatform } from "components/utils/PlatformContext";
import SavedSuccessfulPanel from "../utils/pop-up-panels/saved-successful-pop-up";
import AddNewStudent from "../utils/create-edit-student";
import AddParentPanel from "../utils/pop-up-panels/add-parent-pop-up";
import ConfirmDeletionPanel from "../utils/pop-up-panels/confirm-deletion-pop-up";
import ImportStudentsPopUp from "../utils/pop-up-panels/import-students-pop-up";
import FilterWrapper from "../utils/tab-filters/FilterWrapper";
import FilterPill from "../utils/tab-filters/FilterPill";
import CreateEditClass from "../utils/pop-up-panels/create-edit-class-pop-up";
import ViewTeacherList from "../utils/pop-up-panels/view-teacher-list-pop-up";
import CreateEditUser from "../utils/pop-up-panels/common/CreateEditUser";
import { logoutFunction } from "services/authRequests";

interface ClassesProps {
  ref: any;
  headerRef: React.MutableRefObject<any>;
}

const Classes: React.FC<ClassesProps> = forwardRef(({ headerRef }, ref) => {
  // States definition
  const [activeClass, setActiveClass] = useState("classes");
  const [searchQuery, setSearchQuery] = useState("");
  const [showTabSearchBar, setShowTabSearchBar] = useState(true);
  // States for lists
  /**
   * The general version of "data" had to be changed because
   * it was having race conditions with when rendering, and
   * it was throwing erros when the element was rendering before
   * we get the data.
   */
  const [selectedData, setSelectedData] = useState<
    ClassListType | ContactListType | ParentListType | StudentListType | null
  >(null);
  const [classes, setClasses] = useState<ClassListType[] | null>(null);
  const [staff, setStaff] = useState<ContactListType[] | null>(null);
  const [students, setStudents] = useState<StudentListType[] | null>(null);
  const [parents, setParents] = useState<ParentListType[] | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{
    record:
      | ClassListType
      | ContactListType
      | StudentListType
      | ParentListType
      | null;
    itemType: string;
  }>({ record: null, itemType: "" });
  const [showConfirmDeletePanel, setShowConfirmDeletePanel] = useState(false);
  const [createNewStudent, setCreateNewStudent] = useState(false);
  const [showCreateNewClass, setShowCreateNewClass] = useState(false);
  // States for edit pop ups
  const [showEditClass, setShowEditClass] = useState(false);
  const [showEditTeacher, setShowEditTeacher] = useState(false);
  const [showEditParent, setShowEditParent] = useState(false);
  const [showImportStudents, setShowImportStudents] = useState(false);
  const [showStudentSavedSuccessfully, setShowStudentSavedSuccessfully] =
    useState(false);
  // State for view pop up
  const [selectedTeachers, setSelectedTeachers] = useState<ContactListType[]>(
    [],
  );
  const [showTeachersList, setShowTeachersList] = useState(false);
  // Filter states
  const [rolesToFilter, setRolesToFilter] = useState<string[]>([]);
  const [classesToFilter, setClassesToFilter] = useState<string[]>([]);

  // Add student reference definition
  const addStudentRef = useRef(null);

  // Get platform context
  const { classesTerminology, studentsTerminology } = usePlatform();

  // Get the values from the context
  const { userRole, contextSchoolByRole, setShowSideBarMenu } =
    useContext(MainContext);

  /**
   * Define the list of functions that can be used from external components
   * using a reference
   */
  useImperativeHandle(ref, () => ({
    // Outer function to change the tab
    setShowTabSearchBar(bool: boolean) {
      setShowTabSearchBar(bool);
    },
    getActiveClass() {
      return activeClass;
    },
    setCreateNewStudent(bool: boolean) {
      setCreateNewStudent(bool);
    },
    setShowCreateNewClass(bool: boolean) {
      setShowCreateNewClass(bool);
    },
    setShowImportStudents(bool: boolean) {
      setShowImportStudents(bool);
    },
    handleSaveNewStudent() {
      let currentHandler: any;
      if (addStudentRef.current) currentHandler = addStudentRef.current;
      currentHandler.handleSaveNewStudent();
    },
    setSelectedData(
      data:
        | ClassListType
        | TeacherListType
        | ParentListType
        | StudentListType
        | null,
    ) {
      setSelectedData(data);
    },
    onBackButtonClick() {
      onBackButtonClick();
    },
    onDiscard() {
      onBackButtonClick();
    },
    onCreateEditStudent() {
      onCreateEditStudent();
    },
    onCreateClass() {
      onCreateClass();
    },
  }));

  /**
   * Root definition
   */
  useEffect(() => {
    headerRef.current.setHeaderTitle(
      activeClass === "classes"
        ? classesTerminology
        : activeClass === "students"
          ? studentsTerminology
          : capitaliseFirstChar(activeClass),
    );
    headerRef.current.setHeaderDescription(
      `List of current ${
        activeClass === "classes"
          ? classesTerminology.toLowerCase()
          : activeClass === "students"
            ? studentsTerminology.toLowerCase()
            : activeClass
      }`,
    );
  }, []);

  /**
   * Refresh data
   */
  useEffect(() => {
    getData();
    onBackButtonClick();

    // Force data refresh for this case because of the filter
    if (["staff", "students"].includes(activeClass)) {
      // Force class data fetch
      getData(true, "classes");
    }
  }, [activeClass]);

  /**
   * Refresh data states based on changes
   */
  useEffect(() => {
    // Reset the states that could be related to this
    setClasses(null);
    setStaff(null);
    setStudents(null);
    setParents(null);

    // Update the current list
    getData(true);
    // But also the list of classes
    getData(true, "classes");
  }, [contextSchoolByRole]);

  /**
   * Function that defines the behaviour on click back button
   */
  const onBackButtonClick = () => {
    // Show the main tab
    setShowTabSearchBar(true);
    // And set the status
    setCreateNewStudent(false);
    // And force the selected data to null
    setSelectedData(null);

    // Activate the side bar menu
    setShowSideBarMenu(true);

    // (Main Header) Change the titles
    headerRef.current.setHeaderTitle(
      activeClass === "classes"
        ? classesTerminology
        : activeClass === "students"
          ? studentsTerminology
          : capitaliseFirstChar(activeClass),
    );
    headerRef.current.setHeaderDescription(
      `List of current ${
        activeClass === "classes"
          ? classesTerminology.toLowerCase()
          : activeClass === "students"
            ? studentsTerminology.toLowerCase()
            : activeClass
      }`,
    );
    headerRef.current.setShowBackButton(false);

    // (Class Header) And turn off the other buttons
    headerRef.current.options().setSaveButton({ show: false, disabled: true });
    // Activating the create new button
    headerRef.current.options().setShowCreateNewButton(true);
  };

  // Create Student
  const onCreateEditStudent = (
    title = `New ${singlify(studentsTerminology)}`,
    description = singlify(studentsTerminology),
  ) => {
    // Hide the tab and search bar
    setShowTabSearchBar(false);
    // And set the status
    setCreateNewStudent(true);

    // Hide the side bar menu
    setShowSideBarMenu(false);

    // (Main Header) Change the titles
    headerRef.current.setHeaderTitle(title);
    headerRef.current.setHeaderDescription(
      <div className="w-fit flex gap-1">
        <MdOutlinePerson />
        <div>{description}</div>
      </div>,
    );
    headerRef.current.setShowBackButton(true);

    // (Classes Header) Define behaviour
    headerRef.current.options().setShowCreateNewPopUp(false);
    headerRef.current.options().setShowCreateNewButton(false);
    headerRef.current.options().setSaveButton({ show: true, disabled: true });
  };

  // Create class
  const onCreateClass = () => {
    // And open the create class pop up window
    setShowCreateNewClass(true);

    // (Classes Header) Close the pop up panel menu
    headerRef.current.options().setShowCreateNewPopUp(false);
  };

  /**
   * Function applied when we click on cards
   */
  const handleCardClick = (
    card: ClassListType | ContactListType | ParentListType | StudentListType,
  ) => {
    return (event: React.MouseEvent) => {
      // Select the current data
      setSelectedData(card);

      // Action depending on the shown tab
      if (activeClass === "classes") {
        setShowEditClass(true);
      } else if (activeClass === "staff") {
        setShowEditTeacher(true);
      } else if (activeClass === "students") {
        // Define the type of the variable (for TypeScript)
        const studentCard = card as StudentListType;
        // Show the back button
        headerRef.current.setHeaderBackButton(true);
        // Set the same view as when creating a school
        onCreateEditStudent(
          singlify(studentsTerminology),
          `${studentCard.firstName} ${studentCard.lastName}`,
        );
      } else if (activeClass === "parents") {
        setShowEditParent(true);
      }
    };
  };

  /**
   * Function to get the data depending on the class
   * @returns data
   */
  const getData = async (
    forceRefresh = false,
    currentListToUpdate = activeClass,
  ) => {
    // Make sure there is a context school defined to get the data
    if (!contextSchoolByRole) return;

    // Define the response variable
    let response: any;

    if (currentListToUpdate === "classes") {
      // Check if there are classes already loaded
      // (in order to prevent re-loading)
      if (!forceRefresh && classes && classes.length > 0) return;

      // Get the response using this filter
      response = await getCollectionRequest(
        `/api/classes?schoolId=${contextSchoolByRole._id}`,
      );
    } else if (currentListToUpdate === "staff") {
      // Check if there are teachers already loaded
      // (in order to prevent re-loading)
      if (!forceRefresh && staff && staff.length > 0) return;

      // Get the response using this filter
      response = await getCollectionRequest(
        `/api/users/staff?schoolId=${
          contextSchoolByRole && contextSchoolByRole._id
        }`,
      );
    } else if (currentListToUpdate === "students") {
      // Check if there are students already loaded
      // (in order to prevent re-loading)
      if (!forceRefresh && students && students.length > 0) return;

      // Get the response using this filter
      response = await getCollectionRequest(
        `/api/users/students?schoolId=${
          contextSchoolByRole && contextSchoolByRole._id
        }`,
      );
    } else if (currentListToUpdate === "parents") {
      // Check if there are parents already loaded
      // (in order to prevent re-loading)
      if (!forceRefresh && parents && parents.length > 0) return;

      // Get the response using this filter
      response = await getCollectionRequest(
        `/api/users/parents?schoolId=${
          contextSchoolByRole && contextSchoolByRole._id
        }`,
      );
    }

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        await logoutFunction();
      }
      return [];
    }

    // Get the data
    const data = response.content;

    // Assign the data to the corresponding element
    if (currentListToUpdate === "classes") {
      setClasses(data);
    } else if (currentListToUpdate === "staff") {
      setStaff(data);
    } else if (currentListToUpdate === "students") {
      setStudents(data);
    } else if (currentListToUpdate === "parents") {
      setParents(data);
    }

    return data;
  };

  /**
   * Function executed after create or update a student
   */
  const afterCreateEditStudent = () => {
    // Force unselect data
    setSelectedData(null);

    // Show saved successfully pop up
    setShowStudentSavedSuccessfully(true);

    // Click on discard to comeback
    onBackButtonClick();

    // Reset the states that could be related to this
    setClasses(null);
    setStaff(null);
    setStudents(null);
    setParents(null);

    // And refresh the data
    getData(true);
    // And force refresh data for classes
    getData(true, "classes");
  };

  /**
   * Function executed after create or update a class
   */
  const afterCreateClass = () => {
    // Reset the states that could be related to this
    setClasses(null);
    setStaff(null);

    // And refresh the view if we are creating data from the main view
    getData(true);
  };

  /**
   * Function executed after update a class
   */
  const afterEditClass = () => {
    // Force unselect data
    setSelectedData(null);

    // Reset the states that could be related to this
    setClasses(null);
    setStaff(null);

    // And refresh the view if we are creating data from the main view
    getData(true);
  };

  /**
   * Function executed when clicking "add another" after creating a student
   */
  const handleCreateAnotherStudent = () => {
    // Create another student
    onCreateEditStudent();

    // And close this pop up
    setShowStudentSavedSuccessfully(false);
  };

  /**
   * Delete click definition
   */
  const handleDeleteClick = (
    record: ClassListType | ContactListType | StudentListType | ParentListType,
    itemType: string,
  ) => {
    return (event: React.MouseEvent) => {
      // Prevent activate the click on the whole card
      event.stopPropagation();

      // Open the confirm delete panel
      setShowConfirmDeletePanel(true);

      // And set the lesson to delete
      setItemToDelete({ record, itemType });
    };
  };

  /**
   * Function executed when closing the confirm deletion pop up
   */
  const handleCloseConfirmDelete = () => {
    // Close the pop up
    setShowConfirmDeletePanel(false);

    // And reset the lesson to delete
    setItemToDelete({ record: null, itemType: "" });
  };

  /**
   * Function executed when confirming the deletion of the lesson
   */
  const handleClickConfirmDelete = async () => {
    // Get the delete values
    const { record, itemType } = itemToDelete;

    // Check if the record and the lessonType is defined
    if (!(record && itemType)) return;

    // Address definition
    let endpoint = itemType;

    // Check if the itemType is a type of user to redirect to that
    if (itemType === "staff" || itemType === "students") {
      // Need to differentiate between deleting a class admin or customer admin within staff
      if ((record as ContactListType).role === "class admin") {
        endpoint = "users/class-admins";
      } else if ((record as ContactListType).role === "customer admin") {
        endpoint = "users/customer-admins";
      } else if ((record as StudentListType).role === "student") {
        endpoint = "users/students";
      }
    }

    // Execute the request
    const response = await deleteRecordRequest(
      { _id: record._id },
      `/api/${endpoint}`,
    );

    // Check if everything went right with the request
    if (!(response && response.successful)) return;

    // Force and update to the list
    getData(true, itemType);

    // And reset the lesson to delete
    setItemToDelete({ record: null, itemType: "" });
  };

  /**
   * Function executed once the bulk import is finished
   */
  const afterBulkImport = () => {
    // And Force an update to the class and students
    getData(true, "classes");
    getData(true, "students");
  };

  /**
   *
   * @param staffToEdit
   * @returns
   */
  const handleEditStaff = (staffToEdit: ContactListType) => {
    return async (
      data: CreateEditContactType,
      processedData: CreateEditContactType,
    ) => {
      let url = "";
      if (staffToEdit.role === "class admin") {
        url = "/api/users/class-admins";
      } else if (staffToEdit.role === "customer admin") {
        url = "/api/users/customer-admins";
      } else {
        return;
      }

      // Split into customer/class admin based on staffToEdit.role
      const response = await updateRecordRequest(
        { _id: staffToEdit._id, toUpdate: processedData },
        url,
      );

      // Check if the response is successful
      if (!response.successful) {
        // alert(response.message);
        return;
      }

      // Force unselect data
      setSelectedData(null);

      // Reset the states that could be related to this
      setStaff(null);

      // Refresh the data
      getData(true);

      // And close the pop up
      setShowEditTeacher(false);
    };
  };

  /**
   * Function that defines the tab filters depending of the
   * active tab. It receives a reference that is passed to
   * the rendered filter panel.
   * @param ref
   * @returns
   */
  const tabFilters = (ref: React.RefObject<HTMLDivElement>) => {
    switch (activeClass) {
      case "staff":
        return (
          <FilterWrapper filterRef={ref}>
            {classes && classes.length > 0 && (
              <FilterPill
                pilltext={singlify(classesTerminology)}
                options={classes.map((classItem: ClassListType) => {
                  return { text: classItem.name, value: classItem._id };
                })}
                selectedOptions={classesToFilter}
                setSelectedOptions={setClassesToFilter}
              />
            )}
            <FilterPill
              pilltext="Role"
              options={
                userRole === "master admin"
                  ? [
                      { text: "Master admin", value: "master admin" },
                      { text: "Customer admin", value: "customer admin" },
                      { text: "Staff admin", value: "class admin" },
                    ]
                  : [
                      { text: "Customer admin", value: "customer admin" },
                      { text: "Staff admin", value: "class admin" },
                    ]
              }
              selectedOptions={rolesToFilter}
              setSelectedOptions={setRolesToFilter}
            />
          </FilterWrapper>
        );
      case "students":
        return classes && classes.length > 0 ? (
          <FilterWrapper filterRef={ref}>
            <FilterPill
              pilltext={singlify(classesTerminology)}
              options={classes.map((classItem: ClassListType) => {
                return { text: classItem.name, value: classItem._id };
              })}
              selectedOptions={classesToFilter}
              setSelectedOptions={setClassesToFilter}
            />
          </FilterWrapper>
        ) : (
          <></>
        );
      default:
        return <></>;
    }
  };

  /**
   * Function that updates the "hasPassword" field of a class to true after its
   * password has been updated, without the need to refetch all the data
   * @param classId
   * @returns
   */
  const afterSetClassPassword = (classId: string) => {
    if (!classes) return;

    // Create a new array with the updated class
    const updatedClasses = classes.map((classItem) => {
      if (classItem._id === classId) {
        // Return a new object with hasPassword set to true
        return {
          ...classItem,
          hasPassword: true,
        };
      }
      // Return unchanged for other classes
      return classItem;
    });

    // Update the state with the new array
    setClasses(updatedClasses);
  };

  return (
    <div className="w-full self-stretch flex-col justify-start items-start gap-4 inline-flex">
      {showTabSearchBar && (
        <ViewHeader
          searchQuery={searchQuery}
          activeItem={activeClass}
          tabOptions={[
            "Classes",
            "Staff",
            "Students",
            // "Parents", // ADD BACK WHEN PARENTS BECOME RELEVANT AGAIN
          ]}
          showTabOptions={[
            "master admin",
            "customer admin",
            "class admin",
          ].includes(userRole)}
          showSchoolsDropdown={userRole === "master admin"}
          setActiveItem={setActiveClass}
          setSearchQuery={setSearchQuery}
          filters={tabFilters}
          headerRef={headerRef}
        />
      )}
      <div className="w-full h-full flex-col justify-start items-start gap-4 inline-flex overflow-x-hidden overflow-y-auto custom-scroll">
        {!createNewStudent ? (
          <ClassesList
            activeClass={activeClass}
            classes={classes}
            staff={staff}
            students={students}
            parents={parents}
            searchQuery={searchQuery}
            rolesToFilter={rolesToFilter}
            classesToFilter={classesToFilter}
            setShowTeachersList={setShowTeachersList}
            setSelectedTeachers={setSelectedTeachers}
            handleCardClick={handleCardClick}
            handleDeleteClick={handleDeleteClick}
            onCreateEditStudent={onCreateEditStudent}
            onCreateClass={onCreateClass}
          />
        ) : (
          <AddNewStudent
            ref={addStudentRef}
            selectedData={selectedData as StudentListType}
            afterCreateFunction={afterCreateEditStudent}
            headerRef={headerRef}
          />
        )}
      </div>
      {/* Pop up panels */}
      <AnimatePresence mode="wait">
        {/* Create new class pop-up */}
        {showCreateNewClass && (
          <CreateEditClass
            key="create-class"
            handleClosePopUp={() => setShowCreateNewClass(false)}
            afterCreateEditClass={afterCreateClass}
          />
        )}
        {/* Edit class pop-up */}
        {showEditClass && (
          <CreateEditClass
            key="edit-class"
            selectedData={selectedData as ClassListType}
            handleClosePopUp={() => setShowEditClass(false)}
            afterCreateEditClass={afterEditClass}
            afterSetClassPassword={afterSetClassPassword}
          />
        )}
        {/* Saved successful message pop-up */}
        {showStudentSavedSuccessfully && (
          <SavedSuccessfulPanel
            key="saved-student-successful"
            title={`${singlify(studentsTerminology)} Added Successfully`}
            paragraphs={[
              `The new ${singlify(
                studentsTerminology,
              ).toLowerCase()}  has been successfully added to the system.`,
              `If you wish to add another ${singlify(
                studentsTerminology,
              ).toLowerCase()} , click the 'Add Another' button below.`,
            ]}
            createText="Add another"
            onCreateAnother={handleCreateAnotherStudent}
            onClose={() => setShowStudentSavedSuccessfully(false)}
          />
        )}
        {/* View all teachers pop-up */}
        {showTeachersList && (
          <ViewTeacherList
            selectedTeachers={selectedTeachers}
            setShowPopUp={setShowTeachersList}
          />
        )}
        {/* Edit teacher pop-up */}
        {showEditTeacher && (
          <CreateEditUser
            key="edit-teacher"
            userToEdit={selectedData as TeacherListType}
            confirmButtonText="Save Staff"
            showPrimaryOption={false}
            handlePressBack={() => setShowEditTeacher(false)}
            handleSaveFunction={handleEditStaff(
              selectedData as ContactListType,
            )}
          />
        )}
        {/* Edit parent pop-up */}
        {showEditParent && (
          <AddParentPanel
            setShowPopUp={setShowEditParent}
            selectedData={selectedData as ParentListType}
            getData={getData}
            selectedContacts={[]}
            setSelectedContacts={() => {}}
            setSelectedData={setSelectedData}
          />
        )}
        {/* Confirm delete lesson */}
        {showConfirmDeletePanel && (
          <ConfirmDeletionPanel
            title="Confirm Deletion"
            paragraphs={[
              "This action cannot be undone.",
              "All related data will be permanently removed from the system.",
            ]}
            deleteText="Delete"
            onDelete={handleClickConfirmDelete}
            onClose={handleCloseConfirmDelete}
          />
        )}
        {/* Bulk import panel */}
        {showImportStudents && (
          <ImportStudentsPopUp
            setShowPopUp={setShowImportStudents}
            onCreateEditStudent={onCreateEditStudent}
            afterImportFunction={afterBulkImport}
          />
        )}
      </AnimatePresence>
    </div>
  );
});

export default Classes;
